import React from "react";
import {
  MdEdit,
  MdDelete,
  MdForum,
  MdFavorite,
  MdComment,
  MdPushPin,
  MdOutlinePushPin,
} from "react-icons/md";
import { useNavigate, useLocation } from "react-router-dom";
import Card from "components/card";
import { Post, PostType } from "../../types/community";
import { useAuth } from "../../contexts/AuthContext";
import PostTypeBadge from "./PostTypeBadge";
import PostFilters from "./PostFilters";
import FilterDropdown from "../common/FilterDropdown";
import ActiveFilterBadge from "../common/ActiveFilterBadge";
import { useChallenges } from "../../hooks/useChallenges";
import EmptyState from "../common/EmptyState";
import LoadingSpinner from "../common/LoadingSpinner";

interface PostsTableProps {
  posts: Post[];
  loading: boolean;
  onEdit: (post: Post) => void;
  onDelete: (post: Post) => void;
  onLike: (post: Post) => void;
  onUnlike: (post: Post) => void;
  onPin: (post: Post) => void;
  onUnpin: (post: Post) => void;
  activeFilter: PostType | null;
  onFilterChange: (type: PostType | null) => void;
  challengeId?: number | null;
  onChallengeChange?: (challengeId: number | null) => void;
}

const PostsTable: React.FC<PostsTableProps> = ({
  posts,
  loading,
  onEdit,
  onDelete,
  onPin,
  onUnpin,
  activeFilter,
  onFilterChange,
  challengeId,
  onChallengeChange,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { challenges } = useChallenges();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    };
    const formatter = new Intl.DateTimeFormat("en-US", options);
    const formatted = formatter.format(date);

    // Add ordinal suffix to day
    const day = date.getDate();
    const suffix =
      day % 10 === 1 && day !== 11
        ? "st"
        : day % 10 === 2 && day !== 12
        ? "nd"
        : day % 10 === 3 && day !== 13
        ? "rd"
        : "th";

    return formatted.replace(/\d+/, `${day}${suffix}`);
  };

  const truncateText = (text: string, maxLength: number = 150) => {
    if (!text) return "—";
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName?.charAt(0) || ""}${
      lastName?.charAt(0) || ""
    }`.toUpperCase();
  };

  const canEditPost = (post: Post) => {
    return user && (user.id === post.author.id || user.role === "admin");
  };

  const canDeletePost = (post: Post) => {
    return user && (user.id === post.author.id || user.role === "admin");
  };

  const handleViewPost = (post: Post) => {
    navigate(`/admin/community/${post.id}`, {
      state: { post, from: location.search },
    });
  };

  return (
    <Card extra="w-full pb-10 p-3 md:p-6 h-full">
      <header className="relative flex items-center justify-between">
        <div className="text-xl font-bold text-navy-700 dark:text-white">
          Community Posts
        </div>
      </header>

      <div className="mt-4 border-t-[1px] border-solid border-gray-200 pt-4 dark:border-gray-700">
        <PostFilters
          activeFilter={activeFilter}
          onFilterChange={onFilterChange}
        />
      </div>

      {/* Filters */}
      <div className="mt-4 border-t-[1px] border-solid border-gray-200 pt-4 dark:border-gray-700">
        <h3 className="mb-2 text-[15px] font-medium text-gray-700 dark:text-gray-400">
          Filters:
        </h3>
        <div className="flex flex-col flex-wrap gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col flex-wrap gap-3 sm:flex-row">
            <div className="w-full sm:w-auto">
              <FilterDropdown
                label="Challenge"
                options={challenges.map((challenge) => ({
                  value: challenge.id.toString(),
                  label: `${challenge.name} (${challenge.conducted_at_year})`,
                }))}
                value={challengeId?.toString() ?? undefined}
                onChange={(value) => {
                  const parsedValue =
                    value && value !== "" ? parseInt(value) : null;
                  onChallengeChange?.(parsedValue);
                  // Select "challenge" type filter when challenge is selected
                  if (parsedValue !== null && activeFilter !== "challenge") {
                    onFilterChange("challenge");
                  }
                }}
                placeholder="All Challenges"
              />
            </div>
          </div>
        </div>

        {/* Active Filters */}
        {challengeId !== null && (
          <div className="mt-4 text-sm text-gray-700 dark:text-gray-400">
            <p className="mb-1 text-[15px] font-medium">Active filters:</p>
            <div className="flex flex-wrap items-center gap-2">
              {challengeId && (
                <ActiveFilterBadge
                  label={`Challenge: ${
                    challenges.find((c) => c.id === challengeId)?.name || "..."
                  }`}
                  colorScheme="orange"
                  onClear={() => onChallengeChange?.(null)}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {loading && (
        <div className="flex justify-center py-8">
          <LoadingSpinner bare size="lg" />
        </div>
      )}

      {!loading && posts.length === 0 && (
        <EmptyState
          icon={MdForum}
          title="No posts yet"
          description="Get started by creating the first community post."
        />
      )}

      {!loading && posts.length > 0 && (
        <div className="mt-8 hidden overflow-hidden md:block">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
              <thead className="bg-gray-50 dark:bg-navy-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                    Author
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                    Content
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                    Images
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                    Engagement
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                    Posted
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-navy-900">
                {posts.map((post) => {
                  const authorFullName = `${post.author.first_name || ""} ${
                    post.author.last_name || ""
                  }`.trim();
                  const authorDisplayName = authorFullName || post.author.email;
                  const isDeletedUser = !!post.author.deleted_at;

                  return (
                    <tr
                      key={post.id}
                      className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-navy-800 ${
                        post.is_pinned ? "bg-blue-50 dark:bg-blue-900/20" : ""
                      }`}
                      onClick={() => handleViewPost(post)}
                    >
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex-shrink-0">
                            {post.author.profile_image?.full_path ? (
                              <img
                                src={post.author.profile_image.full_path}
                                alt={authorDisplayName}
                                className="h-10 w-10 rounded-full object-cover"
                              />
                            ) : (
                              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-biafit-primary">
                                <span className="text-sm font-medium text-white">
                                  {getInitials(
                                    post.author.first_name,
                                    post.author.last_name
                                  )}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            {isDeletedUser && (
                              <div className="mb-0.5 text-xs font-medium text-red-600 dark:text-red-400">
                                Deleted User
                              </div>
                            )}
                            <div className="flex flex-col items-start gap-0">
                              {post.is_pinned && (
                                <div className="flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700 dark:bg-blue-900/50 dark:text-blue-300">
                                  <MdPushPin className="h-3 w-3" />
                                  <span>Pinned</span>
                                </div>
                              )}
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {authorDisplayName}
                              </div>
                            </div>
                            {post.author.email &&
                              post.author.email !== authorDisplayName && (
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {post.author.email}
                                </div>
                              )}
                          </div>
                        </div>
                      </td>
                      <td className="min-w-[200px] px-6 py-4">
                        <div className="max-w-md text-sm text-gray-900 dark:text-white">
                          {truncateText(post.content)}
                        </div>
                      </td>
                      <td className="min-w-[170px] px-6 py-4">
                        <div className="flex flex-col gap-2">
                          <PostTypeBadge type={post.type} size="sm" />
                          {post.type === "challenge" && post.challenge && (
                            <div className="px-1 text-xs font-medium text-orange-800 dark:text-orange-200">
                              {post.challenge.name} (
                              {post.challenge.conducted_at_year})
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        {post.attachments && post.attachments.length > 0 && (
                          <div className="relative h-16 w-16">
                            <img
                              src={post.attachments[0].full_path}
                              alt="Post attachment"
                              className="h-16 w-16 rounded-lg border border-gray-200 object-cover dark:border-gray-700"
                            />
                            {post.attachments.length > 1 && (
                              <div className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-biafit-primary text-xs font-semibold text-white dark:border-navy-900">
                                +{post.attachments.length - 1}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-1">
                            <MdFavorite className="h-4 w-4 text-red-500" />
                            <span className="text-sm text-gray-900 dark:text-white">
                              {post.likes_count}
                            </span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <MdComment className="h-4 w-4 text-blue-500" />
                            <span className="text-sm text-gray-900 dark:text-white">
                              {post.comments_count}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {formatDate(post.created_at)}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {user?.role === "admin" && (
                            <button
                              onClick={(event) => {
                                event.stopPropagation();
                                post.is_pinned ? onUnpin(post) : onPin(post);
                              }}
                              className={`transition-colors ${
                                post.is_pinned
                                  ? "text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                                  : "text-gray-600 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                              }`}
                              title={post.is_pinned ? "Unpin Post" : "Pin Post"}
                            >
                              {post.is_pinned ? (
                                <MdPushPin className="h-5 w-5" />
                              ) : (
                                <MdOutlinePushPin className="h-5 w-5" />
                              )}
                            </button>
                          )}
                          {canEditPost(post) && (
                            <button
                              onClick={(event) => {
                                event.stopPropagation();
                                onEdit(post);
                              }}
                              className="text-gray-600 transition-colors hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                              title="Edit Post"
                            >
                              <MdEdit className="h-5 w-5" />
                            </button>
                          )}
                          {canDeletePost(post) && (
                            <button
                              onClick={(event) => {
                                event.stopPropagation();
                                onDelete(post);
                              }}
                              className="text-gray-600 transition-colors hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                              title="Delete Post"
                            >
                              <MdDelete className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && posts.length > 0 && (
        <div className="mt-6 space-y-4 md:hidden">
          {posts.map((post) => {
            const authorFullName = `${post.author.first_name || ""} ${
              post.author.last_name || ""
            }`.trim();
            const authorDisplayName = authorFullName || post.author.email;
            const isDeletedUser = !!post.author.deleted_at;

            return (
              <div
                key={post.id}
                className={`cursor-pointer rounded-lg border border-gray-200 bg-white p-2 dark:border-gray-700 dark:bg-navy-900 md:p-4 ${
                  post.is_pinned
                    ? "border-blue-200 bg-blue-50 dark:border-blue-700 dark:bg-blue-900/20"
                    : ""
                }`}
                onClick={() => handleViewPost(post)}
              >
                <div className="mb-3 flex items-start justify-between">
                  <div className="flex flex-1 items-start space-x-3">
                    <div className="flex-shrink-0">
                      {post.author.profile_image?.full_path ? (
                        <img
                          src={post.author.profile_image.full_path}
                          alt={authorDisplayName}
                          className="h-10 w-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-biafit-primary">
                          <span className="text-sm font-medium text-white">
                            {getInitials(
                              post.author.first_name,
                              post.author.last_name
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      {isDeletedUser && (
                        <p className="mb-0.5 text-xs font-medium text-red-600 dark:text-red-400">
                          Deleted User
                        </p>
                      )}
                      <div className="flex flex-col items-start gap-0">
                        {post.is_pinned && (
                          <div className="flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700 dark:bg-blue-900/50 dark:text-blue-300">
                            <MdPushPin className="h-3 w-3" />
                            <span>Pinned</span>
                          </div>
                        )}
                        <h3 className="font-medium text-navy-700 dark:text-white">
                          {authorDisplayName}
                        </h3>
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(post.created_at)}
                      </p>
                      {post.author.email &&
                        post.author.email !== authorDisplayName && (
                          <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                            {post.author.email}
                          </p>
                        )}
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <PostTypeBadge type={post.type} size="sm" />
                        {post.type === "challenge" && post.challenge && (
                          <div className="rounded-full bg-orange-100 px-2 py-1 text-xs font-medium text-orange-800 dark:bg-orange-900/50 dark:text-orange-200">
                            {post.challenge.name} (
                            {post.challenge.conducted_at_year})
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-center gap-2 md:flex-row">
                    {user?.role === "admin" && (
                      <button
                        onClick={(event) => {
                          event.stopPropagation();
                          post.is_pinned ? onUnpin(post) : onPin(post);
                        }}
                        className={`p-1 transition-colors ${
                          post.is_pinned
                            ? "text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                            : "text-gray-600 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                        }`}
                        title={post.is_pinned ? "Unpin Post" : "Pin Post"}
                      >
                        {post.is_pinned ? (
                          <MdPushPin className="h-5 w-5" />
                        ) : (
                          <MdOutlinePushPin className="h-5 w-5" />
                        )}
                      </button>
                    )}
                    {canEditPost(post) && (
                      <button
                        onClick={(event) => {
                          event.stopPropagation();
                          onEdit(post);
                        }}
                        className="p-1 text-gray-600 transition-colors hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                      >
                        <MdEdit className="h-5 w-5" />
                      </button>
                    )}
                    {canDeletePost(post) && (
                      <button
                        onClick={(event) => {
                          event.stopPropagation();
                          onDelete(post);
                        }}
                        className="p-1 text-gray-600 transition-colors hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                      >
                        <MdDelete className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </div>

                <div className="mb-3">
                  {post.attachments && post.attachments.length > 0 && (
                    <div className="relative h-auto w-full">
                      <img
                        src={post.attachments[0].full_path}
                        alt="Post attachment"
                        className="h-auto w-full rounded-lg border border-gray-200 object-cover dark:border-gray-700"
                      />
                      {post.attachments.length > 1 && (
                        <div className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white bg-biafit-primary text-xs font-semibold text-white dark:border-navy-900">
                          +{post.attachments.length - 1}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="mb-3 text-sm text-gray-600 dark:text-gray-400">
                  {truncateText(post.content, 200)}
                </div>

                <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                  <div className="flex items-center space-x-1">
                    <MdFavorite className="h-4 w-4 text-red-500" />
                    <span>{post.likes_count} likes</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <MdComment className="h-4 w-4 text-blue-500" />
                    <span>{post.comments_count} comments</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

export default PostsTable;
